import React, { useState, useEffect, useRef } from 'react';
import { softDelete } from '../utils/softDelete';
import { supabase } from '../supabase';
import ClienteDetalle from './ClienteDetalle';
import { useEsAdmin } from '../UserContext';
import { emitEntidadChanged, onEntidadChanged } from '../utils/entidadBus';
import { onDataChanged } from '../utils/dataBus';

export default function Clientes() {
  const esAdmin = useEsAdmin();
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [mostrarPotenciales, setMostrarPotenciales] = useState(false);
  const [mostrarInactivos, setMostrarInactivos] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [toast, setToast] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [clienteSeleccionado, setClienteSeleccionado] = useState(null);
  const [modalConfirmOpen, setModalConfirmOpen] = useState(null);
  const [orden, setOrden] = useState('mas_nuevos');
  const mouseDownPos = useRef(null);

  const [form, setForm] = useState({
    nombre: '', apellido: '', dni: '', cuil: '', email: '',
    fecha_nacimiento: '', fecha_primera_consulta: '',
    estado: 'activo',
    telefonos: [{ tipo: 'Celular', numero: '' }],
    domicilios: [{ tipo: 'Real', direccion: '' }]
  });

  useEffect(() => { fetchClientes(); }, []);

  // Refrescar conteos de expedientes/trámites/tareas si cambian desde otro lugar
  useEffect(() => onEntidadChanged((d) => { if (d.tipo === 'expediente' || d.tipo === 'tramite') fetchClientes(); }), []);

  // Automático: cualquier escritura sobre clientes o sus vínculos/conteos
  useEffect(() => onDataChanged(() => fetchClientes(), ['clientes', 'expedientes_clientes', 'tramites_clientes', 'tareas', 'agenda']), []);

  async function fetchClientes() {
    setLoading(true);
    const { data } = await supabase
      .from('clientes')
      .select('*, clientes_telefonos(*), clientes_domicilios(*)')
      .eq('eliminado', false)
      .order('created_at', { ascending: false });

    if (!data?.length) { setClientes([]); setLoading(false); return; }

    const ids = data.map(c => c.id);

    const [{ data: expLinks }, { data: tramLinks }, { data: agendaItems }, { data: tareasItems }] = await Promise.all([
      supabase.from('expedientes_clientes').select('cliente_id, expediente_id').in('cliente_id', ids),
      supabase.from('tramites_clientes').select('cliente_id, tramite_id').in('cliente_id', ids),
      supabase.from('agenda').select('cliente_id, expediente_id').gte('fecha', new Date().toISOString().split('T')[0]).eq('eliminado', false),
      supabase.from('tareas').select('expediente_id, tramite_id').eq('completada', false).eq('eliminado', false),
    ]);

    // Mapa expediente_id → Set de cliente_ids
    const expToClientes = {};
    expLinks?.forEach(({ expediente_id, cliente_id }) => {
      if (!expToClientes[expediente_id]) expToClientes[expediente_id] = new Set();
      expToClientes[expediente_id].add(cliente_id);
    });

    // Mapa tramite_id → Set de cliente_ids
    const tramToClientes = {};
    tramLinks?.forEach(({ tramite_id, cliente_id }) => {
      if (!tramToClientes[tramite_id]) tramToClientes[tramite_id] = new Set();
      tramToClientes[tramite_id].add(cliente_id);
    });

    const idSet = new Set(ids);
    const addCount = (map, clienteId) => {
      if (idSet.has(clienteId)) map[clienteId] = (map[clienteId] || 0) + 1;
    };

    // Conteo de expedientes y trámites
    const expMap = {}, tramMap = {};
    expLinks?.forEach(({ cliente_id }) => addCount(expMap, cliente_id));
    tramLinks?.forEach(({ cliente_id }) => addCount(tramMap, cliente_id));

    // Conteo de agenda: directo por cliente_id O a través de expediente_id
    const agendaMap = {};
    agendaItems?.forEach(({ cliente_id, expediente_id }) => {
      const afectados = new Set();
      if (cliente_id && idSet.has(cliente_id)) afectados.add(cliente_id);
      if (expediente_id && expToClientes[expediente_id]) {
        expToClientes[expediente_id].forEach(cid => { if (idSet.has(cid)) afectados.add(cid); });
      }
      afectados.forEach(cid => addCount(agendaMap, cid));
    });

    // Conteo de tareas pendientes a través de expediente o trámite
    const tareasMap = {};
    tareasItems?.forEach(({ expediente_id, tramite_id }) => {
      const afectados = new Set();
      if (expediente_id && expToClientes[expediente_id]) {
        expToClientes[expediente_id].forEach(cid => { if (idSet.has(cid)) afectados.add(cid); });
      }
      if (tramite_id && tramToClientes[tramite_id]) {
        tramToClientes[tramite_id].forEach(cid => { if (idSet.has(cid)) afectados.add(cid); });
      }
      afectados.forEach(cid => addCount(tareasMap, cid));
    });

    setClientes(data.map(c => ({
      ...c,
      _exp_count:    expMap[c.id]    || 0,
      _tram_count:   tramMap[c.id]   || 0,
      _agenda_count: (agendaMap[c.id] || 0) + (tareasMap[c.id] || 0),
    })));
    setLoading(false);
  }

  function showToast(msg) { setToast(msg); setTimeout(() => setToast(null), 3000); }

  function openDrawer(cliente) {
    setClienteSeleccionado(cliente.id);
    setDrawerOpen(true);
  }

  function openNew() {
    setEditingId(null);
    setForm({
      nombre: '', apellido: '', dni: '', cuil: '', email: '',
      fecha_nacimiento: '', fecha_primera_consulta: '',
      estado: 'activo',
      telefonos: [{ tipo: 'Celular', numero: '' }],
      domicilios: [{ tipo: 'Real', direccion: '' }]
    });
    setModalOpen(true);
  }

  function openEdit(cliente, e) {
    e.stopPropagation();
    setEditingId(cliente.id);
    setForm({
      nombre: cliente.nombre || '', apellido: cliente.apellido || '',
      dni: cliente.dni || '', cuil: cliente.cuil || '', email: cliente.email || '',
      fecha_nacimiento: cliente.fecha_nacimiento || '',
      fecha_primera_consulta: cliente.fecha_primera_consulta || '',
      estado: cliente.estado || 'activo',
      telefonos: cliente.clientes_telefonos?.length ? cliente.clientes_telefonos : [{ tipo: 'Celular', numero: '' }],
      domicilios: cliente.clientes_domicilios?.length ? cliente.clientes_domicilios : [{ tipo: 'Real', direccion: '' }]
    });
    setModalOpen(true);
  }

  async function saveCliente() {
    if (guardando) return;
    if (!form.nombre.trim()) { showToast('El nombre es obligatorio'); return; }
    setGuardando(true);
    const clienteData = {
      nombre: form.nombre, apellido: form.apellido, dni: form.dni,
      cuil: form.cuil, email: form.email,
      fecha_nacimiento: form.fecha_nacimiento || null,
      fecha_primera_consulta: form.fecha_primera_consulta || null,
      estado: form.estado
    };
    let clienteId = editingId;
    if (editingId) {
      await supabase.from('clientes').update(clienteData).eq('id', editingId);
      await supabase.from('clientes_telefonos').delete().eq('cliente_id', editingId);
      await supabase.from('clientes_domicilios').delete().eq('cliente_id', editingId);
    } else {
      const result = await supabase.from('clientes').insert(clienteData).select('id').single();
      if (!result.data || result.error) { showToast('Error al guardar'); return; }
      clienteId = result.data.id;
    }
    const tels = form.telefonos.filter(t => t.numero.trim());
    if (tels.length) await supabase.from('clientes_telefonos').insert(tels.map(t => ({ tipo: t.tipo, numero: t.numero, cliente_id: clienteId })));
    const doms = form.domicilios.filter(d => d.direccion.trim());
    if (doms.length) await supabase.from('clientes_domicilios').insert(doms.map(d => ({ tipo: d.tipo, direccion: d.direccion, cliente_id: clienteId })));
    setGuardando(false);
    setModalOpen(false);
    fetchClientes();
  }

 async function deleteCliente(id, e) {
  e.stopPropagation();
  const cliente = clientes.find(c => c.id === id);
  setModalConfirmOpen({ id, nombre: `${cliente.nombre} ${cliente.apellido}` });
}

async function confirmarDeleteCliente(accion, id, nombre) {
  setModalConfirmOpen(null);
  if (accion === 'eliminar') {
    // Borrar registros relacionados primero para evitar errores de foreign key
    // Junction tables: hard delete (no tienen datos propios importantes)
    await supabase.from('clientes_telefonos').delete().eq('cliente_id', id);
    await supabase.from('clientes_domicilios').delete().eq('cliente_id', id);
    await supabase.from('expedientes_clientes').delete().eq('cliente_id', id);
    await supabase.from('tramites_clientes').delete().eq('cliente_id', id);
    const { error } = await softDelete('clientes', id);
    if (error) { showToast('Error al eliminar cliente: ' + error.message); return; }
    showToast('Cliente enviado a la papelera');
    emitEntidadChanged({ tipo: 'cliente', id, eliminado: true });
    setDrawerOpen(false);
    setClienteSeleccionado(null);
    fetchClientes();
  } else if (accion === 'inactivo') {
    await supabase.from('clientes').update({ estado: 'inactivo' }).eq('id', id);
    showToast('Cliente marcado como inactivo ✓');
    fetchClientes();
  }
}

  function addTelefono() { setForm(f => ({ ...f, telefonos: [...f.telefonos, { tipo: 'Celular', numero: '' }] })); }
  function removeTelefono(i) { setForm(f => ({ ...f, telefonos: f.telefonos.filter((_, idx) => idx !== i) })); }
  function updateTelefono(i, field, value) {
    setForm(f => { const tels = [...f.telefonos]; tels[i] = { ...tels[i], [field]: value }; return { ...f, telefonos: tels }; });
  }
  function addDomicilio() { setForm(f => ({ ...f, domicilios: [...f.domicilios, { tipo: 'Real', direccion: '' }] })); }
  function removeDomicilio(i) { setForm(f => ({ ...f, domicilios: f.domicilios.filter((_, idx) => idx !== i) })); }
  function updateDomicilio(i, field, value) {
    setForm(f => { const doms = [...f.domicilios]; doms[i] = { ...doms[i], [field]: value }; return { ...f, domicilios: doms }; });
  }

  const filtered = clientes.filter(c => {
    const s = search.toLowerCase();
    const matchSearch = !s || `${c.nombre} ${c.apellido}`.toLowerCase().includes(s) || c.dni?.includes(s) || c.email?.toLowerCase().includes(s);
    if (c.estado === 'inactivo' && !mostrarInactivos) return false;
    if (c.estado === 'potencial' && !mostrarPotenciales) return false;
    return matchSearch;
  }).sort((a, b) => {
    switch (orden) {
      case 'alfabetico': {
        const ka = `${a.apellido ? a.apellido + ' ' : ''}${a.nombre || ''}`;
        const kb = `${b.apellido ? b.apellido + ' ' : ''}${b.nombre || ''}`;
        return ka.localeCompare(kb, 'es');
      }
      case 'alfabetico_inv': {
        const ka = `${a.apellido ? a.apellido + ' ' : ''}${a.nombre || ''}`;
        const kb = `${b.apellido ? b.apellido + ' ' : ''}${b.nombre || ''}`;
        return kb.localeCompare(ka, 'es');
      }
      case 'mas_nuevos':
        return (b.created_at || '').localeCompare(a.created_at || '');
      case 'mas_antiguos':
        return (a.created_at || '').localeCompare(b.created_at || '');
      case 'exp_tram_mayor':
        return (b._exp_count + b._tram_count) - (a._exp_count + a._tram_count);
      case 'exp_tram_menor':
        return (a._exp_count + a._tram_count) - (b._exp_count + b._tram_count);
      case 'tareas_mayor':
        return b._agenda_count - a._agenda_count;
      case 'tareas_menor':
        return a._agenda_count - b._agenda_count;
      default:
        return 0;
    }
  });

  const estadoBadge = (e) => {
    const map = { activo: 'badge-green', inactivo: 'badge-red', potencial: 'badge-amber' };
    return <span className={`badge ${map[e] || 'badge-gray'}`}>{e}</span>;
  };

  return (
    <div className="page">
      <div className="page-topbar">
        <div className="flex" style={{ alignItems: 'center' }}>
          <span className="page-title">Clientes</span>
          <span className="page-subtitle">— {filtered.length} registrado{filtered.length !== 1 ? 's' : ''}</span>
        </div>
        <button className="btn btn-primary" onClick={openNew}>+ Nuevo cliente</button>
      </div>

      <div className="page-content">
        <div className="toolbar">
          <div className="search-box">
            <span style={{ color: 'var(--text4)' }}>⌕</span>
            <input placeholder="Buscar por nombre, DNI, email..." value={search} onChange={e => setSearch(e.target.value)} autoComplete="off" spellCheck={false} />
            {search && <button onClick={() => setSearch('')} style={{ background: 'none', border: 'none', color: 'var(--text4)', cursor: 'pointer', padding: '0 2px', fontSize: 16, lineHeight: 1, flexShrink: 0 }}>×</button>}
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--text3)', cursor: 'pointer', userSelect: 'none' }}>
            <input type="checkbox" checked={mostrarPotenciales} onChange={e => setMostrarPotenciales(e.target.checked)} />
            Potenciales
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--text3)', cursor: 'pointer', userSelect: 'none' }}>
            <input type="checkbox" checked={mostrarInactivos} onChange={e => setMostrarInactivos(e.target.checked)} />
            Inactivos
          </label>
        </div>

        <div className="card">
          {/* Ordenar por — dentro del card, encima de la tabla */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px' }}>
            <span style={{ fontSize: 12, color: 'var(--text4)', fontWeight: 500 }}>Ordenar por:</span>
            <select
              className="filter-select"
              value={orden}
              onChange={e => setOrden(e.target.value)}
              style={{ fontSize: 12, minWidth: 220 }}
            >
              <option value="alfabetico">Alfabético (A → Z)</option>
              <option value="alfabetico_inv">Alfabético (Z → A)</option>
              <option value="mas_nuevos">Creación (más recientes)</option>
              <option value="mas_antiguos">Creación (más antiguos)</option>
              <option value="exp_tram_mayor">Mayor cantidad de expedientes y trámites</option>
              <option value="exp_tram_menor">Menor cantidad de expedientes y trámites</option>
              <option value="tareas_mayor">Más tareas pendientes</option>
              <option value="tareas_menor">Menos tareas pendientes</option>
            </select>
          </div>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Estado</th>
                  <th>Expedientes</th>
                  <th>Trámites</th>
                  <th>Tareas y Audiencias</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="5" style={{ textAlign: 'center', padding: 40, color: 'var(--text4)' }}>Cargando...</td></tr>
                ) : filtered.length === 0 ? (
                  <tr><td colSpan="5">
                    <div className="empty-state">
                      <div className="empty-state-icon">👤</div>
                      <div className="empty-state-title">No hay clientes</div>
                      <div>Creá el primero con el botón "Nuevo cliente"</div>
                    </div>
                  </td></tr>
                ) : filtered.map(c => (
                  <tr key={c.id} style={{ cursor: 'pointer' }}
                    onMouseDown={ev => { mouseDownPos.current = { x: ev.clientX, y: ev.clientY }; }}
                    onClick={ev => {
                      if (mouseDownPos.current) {
                        const dx = Math.abs(ev.clientX - mouseDownPos.current.x);
                        const dy = Math.abs(ev.clientY - mouseDownPos.current.y);
                        if (dx > 5 || dy > 5) return;
                      }
                      openDrawer(c);
                    }}>
                    <td>
                      <div className="td-primary">{[c.apellido, c.nombre].filter(Boolean).join(', ')}</div>
                    </td>
                    <td>{estadoBadge(c.estado)}</td>
                    <td style={{ textAlign: 'center' }}>
                      <span style={{ display: 'inline-block', minWidth: 28, padding: '2px 10px', borderRadius: 6, fontSize: 12, fontWeight: 600, background: c._exp_count ? 'var(--blue-light)' : 'var(--surface2)', color: c._exp_count ? 'var(--blue)' : 'var(--text4)', border: `0.5px solid ${c._exp_count ? '#bfdbfe' : 'var(--border)'}` }}>
                        {c._exp_count}
                      </span>
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <span style={{ display: 'inline-block', minWidth: 28, padding: '2px 10px', borderRadius: 6, fontSize: 12, fontWeight: 600, background: c._tram_count ? '#ede9fe' : 'var(--surface2)', color: c._tram_count ? '#7c3aed' : 'var(--text4)', border: `0.5px solid ${c._tram_count ? '#ddd6fe' : 'var(--border)'}` }}>
                        {c._tram_count}
                      </span>
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <span style={{ display: 'inline-block', minWidth: 28, padding: '2px 10px', borderRadius: 6, fontSize: 12, fontWeight: 600, background: c._agenda_count ? '#fef3c7' : 'var(--surface2)', color: c._agenda_count ? '#92400e' : 'var(--text4)', border: `0.5px solid ${c._agenda_count ? '#fde68a' : 'var(--border)'}` }}>
                        {c._agenda_count}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* DRAWER */}
      {drawerOpen && (
        <>
          <div className="drawer-overlay" onMouseDown={() => setDrawerOpen(false)} />
          <div className="drawer">
            <ClienteDetalle
              clienteId={clienteSeleccionado}
              onClose={() => setDrawerOpen(false)}
              onEdit={(cliente) => openEdit(cliente, { stopPropagation: () => {} })}
              onUpdate={fetchClientes}
            />
          </div>
        </>
      )}

      {/* MODAL */}
      {modalOpen && (
        <div className="modal-overlay" onMouseDown={e => e.target === e.currentTarget && setModalOpen(false)}>
          <div className="modal" style={{ width: 620 }}>
            <div className="modal-header" style={{ padding: '13px 20px' }}>
              <div className="modal-title">{editingId ? 'Editar cliente' : 'Nuevo cliente'}</div>
              <button className="modal-close" onClick={() => setModalOpen(false)}>✕</button>
            </div>
            <div className="modal-body" style={{ padding: '10px 20px', overflowY: 'hidden' }}>
              <div className="form-grid" style={{ gap: 8 }}>
                <div className="form-group" style={{ gap: 3 }}>
                  <label className="form-label">Nombre *</label>
                  <input className="form-input" value={form.nombre} onChange={e => setForm(f => ({ ...f, nombre: e.target.value }))} placeholder="Nombre" />
                </div>
                <div className="form-group" style={{ gap: 3 }}>
                  <label className="form-label">Apellido</label>
                  <input className="form-input" value={form.apellido} onChange={e => setForm(f => ({ ...f, apellido: e.target.value }))} placeholder="Apellido" />
                </div>
                <div className="form-group" style={{ gap: 3 }}>
                  <label className="form-label">DNI</label>
                  <input className="form-input" value={form.dni} onChange={e => setForm(f => ({ ...f, dni: e.target.value }))} placeholder="DNI" />
                </div>
                <div className="form-group" style={{ gap: 3 }}>
                  <label className="form-label">CUIL / CUIT</label>
                  <input className="form-input" value={form.cuil} onChange={e => setForm(f => ({ ...f, cuil: e.target.value }))} placeholder="CUIL / CUIT" />
                </div>
                <div className="form-group" style={{ gap: 3 }}>
                  <label className="form-label">Email</label>
                  <input className="form-input" type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="email@ejemplo.com" />
                </div>
                <div className="form-group" style={{ gap: 3 }}>
                  <label className="form-label">Estado</label>
                  <select className="form-select" value={form.estado} onChange={e => setForm(f => ({ ...f, estado: e.target.value }))}>
                    <option value="activo">Activo</option>
                    <option value="inactivo">Inactivo</option>
                    <option value="potencial">Potencial</option>
                  </select>
                </div>
                <div className="form-group" style={{ gap: 3 }}>
                  <label className="form-label">Fecha de nacimiento</label>
                  <input className="form-input" type="date" value={form.fecha_nacimiento} onChange={e => setForm(f => ({ ...f, fecha_nacimiento: e.target.value }))} />
                </div>
                <div className="form-group" style={{ gap: 3 }}>
                  <label className="form-label">Primera consulta <span style={{ fontWeight: 400, color: 'var(--text4)' }}>(opcional)</span></label>
                  <input className="form-input" type="date" value={form.fecha_primera_consulta} onChange={e => setForm(f => ({ ...f, fecha_primera_consulta: e.target.value }))} />
                </div>
                <div className="form-group form-full" style={{ gap: 3 }}>
                  <label className="form-label">Teléfonos</label>
                  {form.telefonos.map((t, i) => (
                    <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 4 }}>
                      <select className="form-select" style={{ width: 120 }} value={t.tipo} onChange={e => updateTelefono(i, 'tipo', e.target.value)}>
                        <option>Celular</option><option>Fijo</option><option>Laboral</option><option>Otro</option>
                      </select>
                      <input className="form-input" style={{ flex: 1 }} value={t.numero} onChange={e => updateTelefono(i, 'numero', e.target.value)} placeholder="Número" />
                      {form.telefonos.length > 1 && <button className="btn btn-danger btn-sm" onClick={() => removeTelefono(i)}>✕</button>}
                    </div>
                  ))}
                  <button className="btn btn-ghost btn-sm" onClick={addTelefono}>+ Agregar teléfono</button>
                </div>
                <div className="form-group form-full" style={{ gap: 3 }}>
                  <label className="form-label">Domicilios</label>
                  {form.domicilios.map((d, i) => (
                    <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 4 }}>
                      <select className="form-select" style={{ width: 120 }} value={d.tipo} onChange={e => updateDomicilio(i, 'tipo', e.target.value)}>
                        <option>Real</option><option>Legal</option><option>Laboral</option><option>Otro</option>
                      </select>
                      <input className="form-input" style={{ flex: 1 }} value={d.direccion} onChange={e => updateDomicilio(i, 'direccion', e.target.value)} placeholder="Dirección" />
                      {form.domicilios.length > 1 && <button className="btn btn-danger btn-sm" onClick={() => removeDomicilio(i)}>✕</button>}
                    </div>
                  ))}
                  <button className="btn btn-ghost btn-sm" onClick={addDomicilio}>+ Agregar domicilio</button>
                </div>
              </div>
            </div>
            <div className="modal-footer" style={{ padding: '10px 20px', justifyContent: 'space-between' }}>
              <div>
                {esAdmin && editingId && (
                  <button
                    className="btn btn-danger"
                    onClick={(e) => { setModalOpen(false); deleteCliente(editingId, e); }}
                  >
                    🗑 Eliminar cliente
                  </button>
                )}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-ghost" onClick={() => setModalOpen(false)}>Cancelar</button>
                <button className="btn btn-primary" onClick={saveCliente} disabled={guardando}>{guardando ? 'Guardando...' : 'Guardar cliente'}</button>
              </div>
            </div>
          </div>
        </div>
      )}
{modalConfirmOpen && (
  <div className="modal-overlay" onMouseDown={e => e.target === e.currentTarget && setModalConfirmOpen(null)}>
    <div className="modal" style={{ width: 460 }}>
      <div className="modal-header">
        <div className="modal-title">⚠️ Atención</div>
        <button className="modal-close" onClick={() => setModalConfirmOpen(null)}>✕</button>
      </div>
      <div className="modal-body">
        <p style={{ fontSize: 14, color: 'var(--text2)', marginBottom: 12 }}>
          Estás por realizar una acción sobre <strong>{modalConfirmOpen.nombre}</strong>.
        </p>
        <div style={{ background: 'var(--red-light)', border: '0.5px solid #fecaca', borderRadius: 8, padding: '12px 14px', fontSize: 13, color: 'var(--red)', lineHeight: 1.6 }}>
          Borrar un cliente eliminará todos sus vínculos con expedientes, trámites, ingresos y deudas. <strong>Esta acción no se puede deshacer.</strong>
        </div>
        <div style={{ background: 'var(--blue-light)', border: '0.5px solid #bfdbfe', borderRadius: 8, padding: '12px 14px', fontSize: 13, color: 'var(--blue)', lineHeight: 1.6, marginTop: 10 }}>
          💡 Recomendamos marcar el cliente como <strong>Inactivo</strong> en lugar de eliminarlo, para conservar el historial.
        </div>
      </div>
      <div className="modal-footer" style={{ justifyContent: 'space-between' }}>
        {esAdmin && <button className="btn btn-danger" onClick={() => confirmarDeleteCliente('eliminar', modalConfirmOpen.id, modalConfirmOpen.nombre)}>
          🗑 Eliminar definitivamente
        </button>}
        <div className="flex gap-2">
          <button className="btn btn-ghost" onClick={() => setModalConfirmOpen(null)}>Cancelar</button>
          <button className="btn btn-primary" onClick={() => confirmarDeleteCliente('inactivo', modalConfirmOpen.id, modalConfirmOpen.nombre)}>
            Marcar como inactivo
          </button>
        </div>
      </div>
    </div>
  </div>
)}
      {toast && <div className="toast">✓ {toast}</div>}
    </div>
  );
}